import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { motion, AnimatePresence } from "framer-motion";
import TickerChip from "./TickerChip";

const TIER_ORDER = ["S","A","B","C","D","F"];
const TIER_STYLES = {
  S: { bg: "rgba(251, 191, 36, 0.10)", text: "#FBBF24", border: "#FBBF24" },
  A: { bg: "rgba(74, 222, 128, 0.10)", text: "#4ADE80", border: "#4ADE80" },
  B: { bg: "rgba(96, 165, 250, 0.10)", text: "#60A5FA", border: "#60A5FA" },
  C: { bg: "rgba(253, 224, 71, 0.10)", text: "#FDE047", border: "#FDE047" },
  D: { bg: "rgba(251, 146, 60, 0.10)", text: "#FB923C", border: "#FB923C" },
  F: { bg: "rgba(248, 113, 113, 0.10)", text: "#F87171", border: "#F87171" },
};

export default function TierList({ tiers, onDragEnd, onTickerClick, revealKey }) {
  // tiers: { S: [{ticker, company, one_liner}], A: [...], ... }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div
        className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.015]"
        data-testid="tier-list"
      >
        {TIER_ORDER.map((tier, idx) => {
          const items = tiers[tier] || [];
          const s = TIER_STYLES[tier];
          return (
            <div
              key={tier}
              className="flex flex-col md:flex-row min-h-[110px] border-b border-white/10 last:border-b-0"
              data-testid={`tier-row-${tier}`}
            >
              <div
                className="flex w-full items-center justify-center border-b border-white/10 p-4 md:w-24 md:border-b-0 md:border-r"
                style={{ background: s.bg, color: s.text, borderColor: s.border + "55" }}
              >
                <span className="tier-label" style={{ color: s.text, textShadow: `0 0 18px ${s.text}55` }}>
                  {tier}
                </span>
              </div>
              <Droppable droppableId={tier} direction="horizontal">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex flex-1 flex-wrap items-start content-start gap-3 p-4 ${snapshot.isDraggingOver ? "bg-white/[0.04]" : ""}`}
                  >
                    <AnimatePresence>
                      {items.map((t, i) => (
                        <Draggable
                          key={`${revealKey}-${t.ticker}-${i}`}
                          draggableId={`${t.ticker}-${i}`}
                          index={i}
                        >
                          {(prov, snap) => (
                            <motion.div
                              initial={{ opacity: 0, y: -24 }}
                              animate={{
                                opacity: 1, y: 0,
                                transition: { delay: idx * 0.12 + i * 0.07, type: "spring", stiffness: 260, damping: 20 },
                              }}
                              exit={{ opacity: 0, scale: 0.9 }}
                            >
                              <TickerChip
                                ticker={t.ticker}
                                company={t.company}
                                onClick={() => onTickerClick(t)}
                                innerRef={prov.innerRef}
                                draggableProps={prov.draggableProps}
                                dragHandleProps={prov.dragHandleProps}
                                isDragging={snap.isDragging}
                              />
                            </motion.div>
                          )}
                        </Draggable>
                      ))}
                    </AnimatePresence>
                    {provided.placeholder}
                    {items.length === 0 && (
                      <span className="font-mono-jb text-xs uppercase tracking-[0.18em] text-zinc-600">
                        drop tickers here
                      </span>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}
